<script setup lang="ts">
import { IconX } from '@tabler/icons-vue'
import { computed } from 'vue'

import BaseBadge from '@tix/ds/components/atoms/BaseBadge.vue'
import BaseButton from '@tix/ds/components/atoms/BaseButton.vue'
import { type Schema } from '@tix/openapi-sdk'

import { formatGalleryImages } from '@/helpers/imageHelpers'

import ImageCarousel from '@/components/ImageCarousel.vue'
import { openDescriptionModal } from '@/components/bundles/openDescriptionModal'

export type BundleInformationProps = {
  name: Schema<'flow_definition_min'>['name']
  meta: Schema<'flow_definition_min'>['meta']
  mode?: 'page' | 'modal'
}

const props = withDefaults(defineProps<BundleInformationProps>(), {
  mode: 'page',
})

// TODO Handle description being too long for summary section?
const summary = computed(() => props.meta.summary || props.meta.description)
// Don't use description if it's being used as summary.
const description = computed(() => (props.meta.summary ? props.meta.description : undefined))

const galleryImages = computed(() => (props.mode === 'modal' ? formatGalleryImages(props.meta.images) : null))
</script>

<template>
  <div class="bundle-info">
    <ImageCarousel v-if="galleryImages?.length" :images="galleryImages" />

    <div class="bundle-heading">
      <h2 class="name">{{ props.name }}</h2>

      <BaseBadge
        v-if="props.meta.sold_out"
        class="sold-out-badge"
        :iconBefore="IconX"
        label="Sold Out"
        size="md"
        type="danger"
        variant="filled"
      />

      <div v-if="props.meta.important_info_1 || props.meta.important_info_2" class="important-info">
        <div v-if="props.meta.important_info_1">{{ props.meta.important_info_1 }}</div>
        <div v-if="props.meta.important_info_2">{{ props.meta.important_info_2 }}</div>
      </div>
    </div>

    <div v-if="summary" class="content">
      <h3>About this event</h3>
      <div v-shtml="summary" class="summary" />

      <template v-if="description">
        <div v-if="mode === 'modal'" v-shtml="description" class="description" />

        <BaseButton v-else class="show-more-button" hierarchy="secondary" @click="openDescriptionModal(description)">
          Show more
        </BaseButton>
      </template>
    </div>
  </div>
</template>

<style scoped>
.bundle-info {
  .bundle-heading {
    display: inline-flex;
    flex-direction: column;
    gap: var(--space-1-5);
    font-size: var(--size-sm);
    margin-top: var(--space-0-75);
    margin-bottom: var(--space-2);

    .name {
      font-weight: 600;
      font-size: 1.5rem;
      margin-bottom: 0;

      @media (--desktop-experience) {
        font-size: 1.875rem;
      }
    }

    .sold-out-badge {
      width: fit-content;
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    padding-top: var(--space-2);
    border-top-style: solid;
    border-top-width: 1px;
    border-top-color: var(--action-card-border);
    margin-bottom: var(--space-0-75);

    > h3 {
      margin-bottom: var(--space-0-75);
      font-weight: 600;
      font-size: 1.125rem;
    }

    .summary {
      font-size: 1rem;
      line-height: 1.6;
      margin-bottom: var(--space-1-5);
    }

    .show-more-button {
      width: 100%;

      @media (--desktop-experience) {
        width: fit-content;
      }
    }

    .description {
      font-size: 1rem;
      line-height: 1.6;
    }
  }
}
</style>
